import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, CircularProgress,
  Alert, MenuItem, Select, FormControl, InputLabel,
  Paper, Chip, LinearProgress,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { sectorService } from '../services/sectorService';
import { COLLECTIONS } from '../firebase/collections';

const PROBLEM_OPTIONS = [
  { value: 'electricity', label: '⚡ Électricité' },
  { value: 'plumbing',    label: '🔧 Plomberie' },
  { value: 'carpentry',   label: '🪚 Menuiserie / Serrurerie' },
  { value: 'painting',    label: '🎨 Peinture / Revêtement' },
  { value: 'hvac',        label: '❄️ Climatisation / Chauffage' },
  { value: 'cleaning',    label: '🧹 Nettoyage / Hygiène' },
  { value: 'it',          label: '💻 Informatique / Réseau' },
  { value: 'safety',      label: '🚨 Sécurité / Urgence' },
  { value: 'other',       label: '📋 Autre' },
];

export default function PublicScanForm() {
  const { token } = useParams();

  const [sector, setSector]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState('');

  const [form, setForm] = useState({
    reporterName: '',
    problem: '',
    description: '',
    room: '',
  });

  // Charger le secteur depuis le token QR
  useEffect(() => {
    async function loadSector() {
      try {
        const data = await sectorService.getSectorByToken(token);
        if (!data) {
          setError('QR code invalide ou expiré. Veuillez contacter votre responsable.');
        } else {
          setSector(data);
        }
      } catch {
        setError('Impossible de charger les informations du secteur.');
      } finally {
        setLoading(false);
      }
    }
    loadSector();
  }, [token]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.problem || !form.description.trim()) {
      setError('Veuillez indiquer le type de problème et une description.');
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      const problemLabel = PROBLEM_OPTIONS.find(p => p.value === form.problem)?.label?.replace(/^[^\s]+\s/, '') || form.problem;

      await addDoc(collection(db, COLLECTIONS.WORK_REQUESTS), {
        // Identification de la source
        title:               `${problemLabel} — ${sector.name}`,
        status:              'pending_validation',
        isAnonymous:         true,
        anonymousName:       form.reporterName.trim() || 'Anonyme',
        sectorId:            sector.id,
        sectorName:          sector.name,
        assignedDemandeurId: sector.demandeurId,
        schoolId:            sector.schoolId,

        // Localisation pré-remplie depuis le secteur
        location: {
          building: sector.building || '',
          floor:    sector.floor    || '',
          room:     form.room.trim(),
        },

        // Détail du problème
        problem:     form.problem,
        description: form.description.trim(),
        priority:    'medium',

        // Demandeur = QR anonyme
        requestingServiceName: `QR — ${sector.name}`,
        assignedServiceId:    null,
        requestingServiceId:  null,
        createdBy:            null,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSubmitted(true);
    } catch (err) {
      console.error('PublicScanForm: erreur soumission', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Écrans de chargement / erreur / succès ──────────────────
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4ff' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !sector) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, background: '#f0f4ff' }}>
        <Paper sx={{ p: 4, maxWidth: 420, textAlign: 'center', borderRadius: 3 }}>
          <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>QR code invalide</Typography>
          <Typography color="text.secondary">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, background: 'linear-gradient(145deg, #f0f4ff, #e8f4ff)' }}>
        <Paper sx={{ p: 4, maxWidth: 420, textAlign: 'center', borderRadius: 3 }} className="animate-scale-in">
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontFamily: "'Syne', sans-serif" }}>
            Signalement envoyé !
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Votre signalement a bien été transmis au responsable du secteur <strong>{sector.name}</strong>.
            Il sera traité dans les meilleurs délais.
          </Typography>
          <Button variant="contained" onClick={() => { setSubmitted(false); setForm({ reporterName: '', problem: '', description: '', room: '' }); }}>
            Nouveau signalement
          </Button>
        </Paper>
      </Box>
    );
  }

  // ── Formulaire principal ────────────────────────────────────
  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(145deg, #0c1120 0%, #1a2540 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
    }}>

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }} className="animate-fade-up">
        <Typography sx={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.6rem', color: '#fff', letterSpacing: '-0.03em' }}>
          EduWorks
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Signalement de problème
        </Typography>
      </Box>

      {/* Secteur badge */}
      <Box sx={{ mb: 2 }} className="animate-fade-up delay-1">
        <Chip
          icon={<LocationOnIcon />}
          label={`${sector.name}${sector.building ? ` — ${sector.building}` : ''}${sector.floor ? `, ${sector.floor}` : ''}`}
          sx={{
            background: 'rgba(59,91,219,0.2)',
            color: '#7ba7ff',
            border: '1px solid rgba(59,91,219,0.3)',
            fontWeight: 600,
            fontSize: '0.85rem',
            py: 0.5,
          }}
        />
      </Box>

      {/* Formulaire */}
      <Paper
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 3,
          p: 3,
          background: 'rgba(255,255,255,0.97)',
        }}
        className="animate-fade-up delay-2"
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {submitting && <LinearProgress sx={{ mb: 2, borderRadius: 99 }} />}

        {/* Nom (optionnel) */}
        <TextField
          fullWidth
          name="reporterName"
          label="Votre nom (optionnel)"
          placeholder="Ex : M. Dupont, Professeur de maths"
          value={form.reporterName}
          onChange={handleChange}
          disabled={submitting}
          sx={{ mb: 2 }}
          size="small"
        />

        {/* Salle / local */}
        <TextField
          fullWidth
          name="room"
          label="Salle / Local concerné (optionnel)"
          placeholder="Ex : Salle 14, Couloir 2e étage, Toilettes"
          value={form.room}
          onChange={handleChange}
          disabled={submitting}
          sx={{ mb: 2 }}
          size="small"
        />

        {/* Type de problème */}
        <FormControl fullWidth sx={{ mb: 2 }} size="small" required>
          <InputLabel>Type de problème *</InputLabel>
          <Select
            name="problem"
            value={form.problem}
            label="Type de problème *"
            onChange={handleChange}
            disabled={submitting}
          >
            {PROBLEM_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Description */}
        <TextField
          fullWidth
          required
          multiline
          rows={4}
          name="description"
          label="Description du problème *"
          placeholder="Décrivez le problème constaté : depuis quand, où exactement, gravité..."
          value={form.description}
          onChange={handleChange}
          disabled={submitting}
          sx={{ mb: 3 }}
          size="small"
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={submitting || !form.problem || !form.description.trim()}
          size="large"
          sx={{ borderRadius: '12px', fontFamily: "'Syne', sans-serif", fontWeight: 700, py: 1.4 }}
        >
          {submitting ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Envoyer le signalement'}
        </Button>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'text.disabled' }}>
          Ce formulaire est anonyme. Votre signalement sera examiné par le responsable du secteur.
        </Typography>
      </Paper>
    </Box>
  );
}
